import React, { useState } from 'react'
import { Col, Row } from 'react-bootstrap'
import classNames from 'classnames'
import { SelectOption } from '../../../../../../../components/SelectDropdown/SelectDropdown'
import { Button } from '../../../../../../../components/Button/Button'
import { FormFieldDefinition, IntegrationObjectRelationship } from '../../../hooks'
import { IntegrationServiceId } from '../../../../../../../common/integrations'
import { FilterCondition } from './types'
import { EditableFilterName } from './EditableFilterName'
import { ConditionRowContainer } from './ConditionRowContainer'
import { ConfirmDeleteFilterModal } from './ConfirmDeleteFilterModal'
import styles from './FilterGroup.module.scss'

export interface FilterCardProps {
  serviceId: IntegrationServiceId
  name: string
  conditions: FilterCondition[]
  availableFormFieldDefinitions: FormFieldDefinition[]
  availableRelationships?: IntegrationObjectRelationship[]
  operatorOptions: SelectOption[]
  loading?: boolean
  onNameChange: (name: string) => void
  onConditionChange: (index: number, patch: Partial<FilterCondition>) => void
  onAddCondition: () => void
  onRemoveCondition: (index: number) => void
  onRemove: () => void
}

export const FilterCard: React.FC<FilterCardProps> = ({
  serviceId,
  name,
  conditions,
  availableFormFieldDefinitions,
  availableRelationships,
  operatorOptions,
  loading,
  onNameChange,
  onConditionChange,
  onAddCondition,
  onRemoveCondition,
  onRemove,
}) => {
  const [collapsed, setCollapsed] = useState<boolean>(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false)

  return (
    <div className='border rounded mb-3'>
      <div
        className={classNames(
          'd-flex flex-column justify-content-center align-items-start gap-4 p-3 zocks-bg-color-gray-100',
          collapsed ? 'rounded' : 'rounded-top',
        )}
      >
        <div className={classNames('d-flex align-items-center gap-2 w-100', styles.cardHeaderRow)}>
          <Button
            type='button'
            className={classNames('p-1', styles.iconButton)}
            onClick={() => setCollapsed((prev) => !prev)}
            aria-label={collapsed ? 'Expand filter' : 'Collapse filter'}
          >
            <i className={classNames('bi', collapsed ? 'bi-chevron-contract' : 'bi-chevron-expand')} />
          </Button>
          <div className='flex-grow-1'>
            <EditableFilterName name={name} onChange={onNameChange} />
          </div>
          <Button type='button' className={classNames('p-2', styles.iconButton)} onClick={() => setShowDeleteConfirm(true)} aria-label='Delete filter'>
            <i className='bi bi-trash' />
          </Button>
        </div>
      </div>

      <ConfirmDeleteFilterModal
        show={showDeleteConfirm}
        onHide={() => setShowDeleteConfirm(false)}
        onConfirm={() => {
          setShowDeleteConfirm(false)
          onRemove()
        }}
        loading={loading}
      />

      {!collapsed && (
        <div className='p-3'>
          <Row className='mb-2'>
            <Col>
              <span className='fw-bold small text-uppercase'>Conditions</span>
            </Col>
          </Row>

          <div className='d-flex flex-column gap-2 mb-3'>
            {conditions.map((condition, index) => (
              <ConditionRowContainer
                key={condition.id}
                serviceId={serviceId}
                condition={condition}
                availableFormFieldDefinitions={availableFormFieldDefinitions}
                availableRelationships={availableRelationships}
                operatorOptions={operatorOptions}
                loading={loading}
                onChange={(patch) => onConditionChange(index, patch)}
                onRemove={() => onRemoveCondition(index)}
              />
            ))}
          </div>

          <Row>
            <Col>
              <Button type='button' variant='primaryGhost' className='border-0' onClick={onAddCondition}>
                + Add condition
              </Button>
            </Col>
          </Row>
        </div>
      )}
    </div>
  )
}
